/**
 * TypeScript binding for the frozen live-translation v1 contracts.
 *
 * Authority: `contracts/live-translation/route.v1.schema.json`,
 * `contracts/live-translation/turn.v1.schema.json` and
 * `contracts/live-translation/conformance-v1.json`. Those files are the
 * cross-runtime source of truth. This module is a BINDING of them, and
 * `contracts.test.ts` runs all 126 shared conformance vectors through it and
 * fails if a single accept/reject decision — or the REASON for a reject —
 * differs from what the JSON Schema declares.
 *
 * Why the schema is embedded rather than imported: the contract files live
 * outside the Next.js project root, so they cannot be bundled for the browser.
 * The embedded copy is therefore compared keyword-for-keyword against the JSON
 * files in the test suite (annotations aside), which makes drift a test
 * failure rather than a production surprise. The language enums are not
 * transcribed at all — they are built from `LAUNCH_LANGUAGE_CODES`, so there is
 * exactly one launch-language list in `ui-frontend`.
 *
 * Why a small validator instead of ajv: these schemas are JSON Schema draft
 * 2020-12, and the ajv installed in this project is 6.x, which is draft-07 only
 * and has no `ajv/dist/2020` entry point. Adding a dependency is out of scope
 * for this task. The interpreter below implements exactly the keyword subset
 * the contracts use and REFUSES TO LOAD a schema containing a keyword it does
 * not implement (`assertSupportedSchemaKeywords`), because silently ignoring a
 * keyword under-validates without ever going red.
 *
 * Error shape is an ajv-compatible subset: `instancePath` (a JSON Pointer, `''`
 * at the root) plus `keyword`. Validation always runs in all-errors mode — ajv
 * defaults to stopping at the first error, which hides the keyword a vector
 * names. `required` and `additionalProperties` errors are reported at the
 * OBJECT's path, as ajv and python-jsonschema both do.
 *
 * DELIBERATELY NOT HERE:
 *  - The deterministic UUIDv5 derivations in `conformance-v1.json#/derivations`
 *    (binder invariants BI-03/BI-04, "the record's id must equal the derivation
 *    over its own fields"). They need a SHA-1 hash core, which is not part of a
 *    schema binding, and duplicating a hash implementation across modules is
 *    exactly the drift hazard this file exists to prevent. No task in the
 *    workflow plan currently owns them — that gap is reported, not papered over.
 *  - Every other binder invariant (BI-01..BI-14): sibling comparisons,
 *    cross-record uniqueness/ordering, stale-generation rejection. JSON Schema
 *    cannot express them and neither can this module; they belong to the writer
 *    and the planner.
 *  - Anything about a translation provider: no event name, endpoint, codec,
 *    cadence, or model identifier appears in this contract. Those fields are
 *    opaque, nullable strings on purpose.
 */

import { LAUNCH_LANGUAGE_CODES, type LaunchLanguageCode } from './launchLanguages';

// ---------------------------------------------------------------------------
// Contract versions
// ---------------------------------------------------------------------------

export const ROUTE_ENVELOPE_V1_CONTRACT_VERSION = 'live-translation.route.v1';
export const SOURCE_TURN_V1_CONTRACT_VERSION = 'live-translation.turn.source-turn.v1';
export const TRANSLATION_REVISION_V1_CONTRACT_VERSION = 'live-translation.turn.translation-revision.v1';

// ---------------------------------------------------------------------------
// Closed vocabularies. Every one of these is a CLOSED SET: adding a member is a
// versioned contract change (publish a v2 schema), never an inline edit. Each
// list is asserted equal to its schema enum in the test suite.
// ---------------------------------------------------------------------------

/**
 * What MAY BE HEARD on a track. Identity describes WHO; media_purpose describes
 * WHAT. Once a non-legacy envelope exists, no audibility decision may read a
 * participant identity or display name (AC-0.4).
 */
export const MEDIA_PURPOSES = [
  'original_source',
  'translated_source',
  'recipient_mix',
  'egress_review_mix',
  'ai_input_mix',
  'avatar_output',
  'dtmf_control',
  'legacy_unknown',
] as const;
export type MediaPurpose = (typeof MEDIA_PURPOSES)[number];

export const RECIPIENT_GROUPS = ['all_participants', 'translation_pipeline', 'egress', 'ai_agents'] as const;
export type RecipientGroup = (typeof RECIPIENT_GROUPS)[number];

/**
 * Observable route states. `revised` is deliberately absent: it is a
 * transcript-level state derived from revision selection, and a revision never
 * replaces audio mid-utterance (BI-12).
 */
export const ROUTE_STATES = [
  'pending',
  'active',
  'translating',
  'delayed',
  'reconnecting',
  'unavailable',
  'draining',
  'ended',
] as const;
export type RouteState = (typeof ROUTE_STATES)[number];

/** Structural role from authenticated participant attributes. `unknown` is the honest legacy value. */
export const SPEAKER_ROLES = ['caller', 'officer', 'ai_agent', 'unknown'] as const;
export type SpeakerRole = (typeof SPEAKER_ROLES)[number];

/** Source turns come from finals only. No translation output ever carries a finality. */
export const FINALITIES = ['deepgram_final', 'synthetic_deepgram_flush'] as const;
export type Finality = (typeof FINALITIES)[number];

export const DELIVERY_STATES = [
  'pending',
  'delivered_live',
  'partial',
  'missed_deadline',
  'not_delivered_live',
] as const;
export type DeliveryState = (typeof DELIVERY_STATES)[number];

/** Structural markers only — never free-text semantic classification, never keyword matching. */
export const NUANCE_KINDS = [
  'code_switched',
  'uncertain_source',
  'idiomatic_rendering',
  'literal_rendering',
  'untranslated_term',
] as const;
export type NuanceKind = (typeof NUANCE_KINDS)[number];

// ---------------------------------------------------------------------------
// Purpose groupings. These ARE the conditional rules in route.v1: the schema
// below is built from these constants, so a route planner that imports them is
// reading the same set the validator enforces, not a paraphrase of it.
// ---------------------------------------------------------------------------

/** Carry no translation, so they must carry no target language. */
export const LANGUAGE_FREE_PURPOSES = ['original_source', 'dtmf_control', 'legacy_unknown'] as const;

/** Untrusted or non-speech, so any language claim attached to them is untrusted too. */
export const LANGUAGE_CLAIM_FREE_PURPOSES = ['dtmf_control', 'legacy_unknown'] as const;

/** Exist to deliver a chosen language, so they must name it. */
export const LANGUAGE_BEARING_PURPOSES = [
  'translated_source',
  'recipient_mix',
  'ai_input_mix',
  'avatar_output',
] as const;

/** Must name the exact source epoch they came from, or stale output cannot be fenced (AC-2.2). */
export const SOURCE_BOUND_PURPOSES = ['original_source', 'translated_source'] as const;

// ---------------------------------------------------------------------------
// Record types. Every field is required and explicitly present; optionality is
// an explicit `null`, never an absent key. A dropped generation is a hard
// reject, not a silently-defaulted zero.
// ---------------------------------------------------------------------------

export interface RouteEnvelopeV1 {
  contract_version: typeof ROUTE_ENVELOPE_V1_CONTRACT_VERSION;
  call_id: string;
  /** Controller-minted. NOT the LiveKit identity, NOT a display name, never parsed for meaning. */
  stable_participant_id: string;
  publication_sid: string;
  source_participant_id: string | null;
  source_track_sid: string | null;
  /** Publication generation of the source track. Starts at 1; 0 is an uninitialised counter. */
  source_epoch: number | null;
  /** Exactly one of recipient_id / recipient_group is non-null. */
  recipient_id: string | null;
  recipient_group: RecipientGroup | null;
  media_purpose: MediaPurpose;
  source_language: LaunchLanguageCode | null;
  target_language: LaunchLanguageCode | null;
  assignment_generation: number;
  route_generation: number;
  state: RouteState;
  created_at: string;
}

export interface SourceTurnV1 {
  contract_version: typeof SOURCE_TURN_V1_CONTRACT_VERSION;
  /** uuid5(namespace, call_id|stable_participant_id|source_epoch|deepgram_segment_id). */
  source_turn_id: string;
  call_id: string;
  stable_participant_id: string;
  source_track_sid: string;
  source_epoch: number;
  ordinal: number;
  speaker_role: SpeakerRole;
  source_language: LaunchLanguageCode | null;
  /**
   * The per-utterance language OBSERVATION from the STT provider — distinct
   * from `source_language`, which is the assignment-backed CLAIM (BI-08). A
   * code-switching speaker makes the claim wrong and this field right.
   * TRANSITIONAL-OPTIONAL while pre-observation producers remain deployed.
   */
  detected_language?: LaunchLanguageCode | null;
  assignment_generation: number | null;
  started_at_ms: number;
  ended_at_ms: number;
  /** Authoritative source text. A frontend transcript message can never mutate or replace it. */
  text: string;
  deepgram_segment_id: string;
  deepgram_confidence: number | null;
  finality: Finality;
  created_at: string;
}

export interface NuanceAnnotation {
  kind: NuanceKind;
  note: string | null;
}

export interface TranslationRevisionV1 {
  contract_version: typeof TRANSLATION_REVISION_V1_CONTRACT_VERSION;
  /** uuid5(namespace, source_turn_id|target_language|revision_index). */
  translation_revision_id: string;
  source_turn_id: string;
  call_id: string;
  target_language: LaunchLanguageCode;
  revision_index: number;
  /** A missing translation is a structural marker elsewhere, never an empty revision. */
  text: string;
  delivery_state: DeliveryState;
  output_started_at_ms: number | null;
  output_ended_at_ms: number | null;
  output_artifact_ref: string | null;
  translation_session_id: string | null;
  translation_response_id: string | null;
  model_version: string | null;
  request_contract_version: string | null;
  assignment_generation: number;
  route_generation: number;
  nuance_annotations: NuanceAnnotation[];
  created_at: string;
}

export type TurnRecordV1 = SourceTurnV1 | TranslationRevisionV1;

export interface JsonSchema {
  [keyword: string]: unknown;
}

// ===========================================================================
// EMBEDDED SCHEMAS
//
// A keyword-for-keyword copy of the frozen JSON Schemas, minus annotations
// ($schema, $id, title, description, $comment). `contracts.test.ts` asserts
// deep equality with the files, so this copy cannot drift.
//
// `format` is deliberately unused: format assertion is opt-in in ajv and off by
// default in python jsonschema, so it would make the two runtimes disagree.
// UUIDs and timestamps are `pattern`-constrained instead.
//
// Nullable fields use the FLAT form `{type: ['x','null'], ...}` rather than a
// `oneOf` wrapper. That is what makes a bad value report `enum` / `minimum` at
// its own property instead of an opaque `oneOf` at the same location — LT-F01
// found the wrapper form breaks error attribution across runtimes.
// ===========================================================================

const UUID_PATTERN = '^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$';
const UUID_V5_PATTERN = '^[0-9a-f]{8}-[0-9a-f]{4}-5[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$';
/** Charset deliberately excludes U+007C so the `|`-joined UUIDv5 derivation name stays injective. */
const OPAQUE_ID_PATTERN = '^[A-Za-z0-9_.:-]{1,128}$';
const TRACK_SID_PATTERN = '^[A-Za-z0-9_-]{1,64}$';
/** RFC 3339 with a mandatory `T` separator and a mandatory offset. */
const RFC3339_PATTERN =
  '^[0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}(\\.[0-9]{1,9})?(Z|[+-][0-9]{2}:[0-9]{2})$';
/** Number.MAX_SAFE_INTEGER: above it two different generations could compare equal in the browser. */
const GENERATION_MAX = Number.MAX_SAFE_INTEGER;
/** 2024-01-01T00:00:00Z — the unit-confusion guard. Seconds passed where ms are expected reject. */
const EPOCH_MILLIS_MIN = 1704067200000;

/** The launch catalog, never re-typed. This is the only place the schemas learn the languages. */
const OUTPUT_LANGUAGE_ENUM: unknown[] = [...LAUNCH_LANGUAGE_CODES];
const OPTIONAL_OUTPUT_LANGUAGE_ENUM: unknown[] = [...LAUNCH_LANGUAGE_CODES, null];

const ROUTE_V1_SCHEMA: JsonSchema = {
  type: 'object',
  additionalProperties: false,
  required: [
    'contract_version',
    'call_id',
    'stable_participant_id',
    'publication_sid',
    'source_participant_id',
    'source_track_sid',
    'source_epoch',
    'recipient_id',
    'recipient_group',
    'media_purpose',
    'source_language',
    'target_language',
    'assignment_generation',
    'route_generation',
    'state',
    'created_at',
  ],
  properties: {
    contract_version: { const: ROUTE_ENVELOPE_V1_CONTRACT_VERSION },
    call_id: { $ref: '#/$defs/Uuid' },
    stable_participant_id: { $ref: '#/$defs/OpaqueId' },
    publication_sid: { $ref: '#/$defs/TrackSid' },
    source_participant_id: { $ref: '#/$defs/OptionalOpaqueId' },
    source_track_sid: { $ref: '#/$defs/OptionalTrackSid' },
    source_epoch: { $ref: '#/$defs/OptionalSourceEpoch' },
    recipient_id: { $ref: '#/$defs/OptionalOpaqueId' },
    recipient_group: { $ref: '#/$defs/OptionalRecipientGroup' },
    media_purpose: { $ref: '#/$defs/MediaPurpose' },
    source_language: { $ref: '#/$defs/OptionalOutputLanguageCode' },
    target_language: { $ref: '#/$defs/OptionalOutputLanguageCode' },
    assignment_generation: { $ref: '#/$defs/Generation' },
    route_generation: { $ref: '#/$defs/Generation' },
    state: { $ref: '#/$defs/RouteState' },
    created_at: { $ref: '#/$defs/Rfc3339Timestamp' },
  },
  allOf: [
    // A route addresses exactly one recipient or exactly one group.
    {
      oneOf: [
        { properties: { recipient_id: { type: 'string' }, recipient_group: { type: 'null' } } },
        { properties: { recipient_id: { type: 'null' }, recipient_group: { type: 'string' } } },
      ],
    },
    // AC-0.4: purposes that carry no translation must carry no target language.
    {
      if: { required: ['media_purpose'], properties: { media_purpose: { enum: [...LANGUAGE_FREE_PURPOSES] } } },
      then: { properties: { target_language: { type: 'null' } } },
    },
    // An untrusted or non-speech purpose makes no language claim at all.
    {
      if: {
        required: ['media_purpose'],
        properties: { media_purpose: { enum: [...LANGUAGE_CLAIM_FREE_PURPOSES] } },
      },
      then: { properties: { source_language: { type: 'null' } } },
    },
    // A route that exists to deliver a chosen language must name it.
    {
      if: { required: ['media_purpose'], properties: { media_purpose: { enum: [...LANGUAGE_BEARING_PURPOSES] } } },
      then: { properties: { target_language: { type: 'string' } } },
    },
    // AC-2.2: a source-bound track must name the exact source epoch it came from.
    {
      if: { required: ['media_purpose'], properties: { media_purpose: { enum: [...SOURCE_BOUND_PURPOSES] } } },
      then: {
        properties: {
          source_participant_id: { type: 'string' },
          source_track_sid: { type: 'string' },
          source_epoch: { type: 'integer' },
        },
      },
    },
    // AC-2.4: a recipient mix is one stable publication per recipient.
    {
      if: { required: ['media_purpose'], properties: { media_purpose: { const: 'recipient_mix' } } },
      then: { properties: { recipient_id: { type: 'string' } } },
    },
    // AC-5.6: the review mix exists only for the recording egress.
    {
      if: { required: ['media_purpose'], properties: { media_purpose: { const: 'egress_review_mix' } } },
      then: { properties: { recipient_group: { const: 'egress' } } },
    },
  ],
  $defs: {
    MediaPurpose: { type: 'string', enum: [...MEDIA_PURPOSES] },
    OptionalRecipientGroup: { type: ['string', 'null'], enum: [...RECIPIENT_GROUPS, null] },
    RouteState: { type: 'string', enum: [...ROUTE_STATES] },
    OutputLanguageCode: { type: 'string', enum: OUTPUT_LANGUAGE_ENUM },
    OptionalOutputLanguageCode: { type: ['string', 'null'], enum: OPTIONAL_OUTPUT_LANGUAGE_ENUM },
    Uuid: { type: 'string', pattern: UUID_PATTERN },
    OpaqueId: { type: 'string', pattern: OPAQUE_ID_PATTERN },
    OptionalOpaqueId: { type: ['string', 'null'], pattern: OPAQUE_ID_PATTERN },
    TrackSid: { type: 'string', pattern: TRACK_SID_PATTERN },
    OptionalTrackSid: { type: ['string', 'null'], pattern: TRACK_SID_PATTERN },
    OptionalSourceEpoch: { type: ['integer', 'null'], minimum: 1, maximum: GENERATION_MAX },
    Generation: { type: 'integer', minimum: 0, maximum: GENERATION_MAX },
    Rfc3339Timestamp: { type: 'string', pattern: RFC3339_PATTERN },
  },
};

const TURN_V1_SCHEMA: JsonSchema = {
  oneOf: [{ $ref: '#/$defs/SourceTurnV1' }, { $ref: '#/$defs/TranslationRevisionV1' }],
  $defs: {
    OutputLanguageCode: { type: 'string', enum: OUTPUT_LANGUAGE_ENUM },
    OptionalOutputLanguageCode: { type: ['string', 'null'], enum: OPTIONAL_OUTPUT_LANGUAGE_ENUM },
    Uuid: { type: 'string', pattern: UUID_PATTERN },
    UuidV5: { type: 'string', pattern: UUID_V5_PATTERN },
    OpaqueId: { type: 'string', pattern: OPAQUE_ID_PATTERN },
    TrackSid: { type: 'string', pattern: TRACK_SID_PATTERN },
    Generation: { type: 'integer', minimum: 0, maximum: GENERATION_MAX },
    OptionalGeneration: { type: ['integer', 'null'], minimum: 0, maximum: GENERATION_MAX },
    EpochMillis: { type: 'integer', minimum: EPOCH_MILLIS_MIN, maximum: GENERATION_MAX },
    OptionalEpochMillis: { type: ['integer', 'null'], minimum: EPOCH_MILLIS_MIN, maximum: GENERATION_MAX },
    Rfc3339Timestamp: { type: 'string', pattern: RFC3339_PATTERN },
    SourceTurnV1: {
      type: 'object',
      additionalProperties: false,
      required: [
        'contract_version',
        'source_turn_id',
        'call_id',
        'stable_participant_id',
        'source_track_sid',
        'source_epoch',
        'ordinal',
        'speaker_role',
        'source_language',
        'assignment_generation',
        'started_at_ms',
        'ended_at_ms',
        'text',
        'deepgram_segment_id',
        'deepgram_confidence',
        'finality',
        'created_at',
      ],
      properties: {
        contract_version: { const: SOURCE_TURN_V1_CONTRACT_VERSION },
        source_turn_id: { $ref: '#/$defs/UuidV5' },
        call_id: { $ref: '#/$defs/Uuid' },
        stable_participant_id: { $ref: '#/$defs/OpaqueId' },
        source_track_sid: { $ref: '#/$defs/TrackSid' },
        source_epoch: { type: 'integer', minimum: 1, maximum: GENERATION_MAX },
        ordinal: { type: 'integer', minimum: 0, maximum: GENERATION_MAX },
        speaker_role: { type: 'string', enum: [...SPEAKER_ROLES] },
        source_language: { $ref: '#/$defs/OptionalOutputLanguageCode' },
        // TRANSITIONAL-OPTIONAL (2026-08-24): the per-utterance language
        // OBSERVATION from the STT provider, distinct from the
        // assignment-backed source_language CLAIM (BI-08). Deliberately NOT in
        // `required` while producers older than the observation chain remain
        // deployed; promoted to required-with-null once the fleet ships. A
        // code-switching speaker makes the claim wrong and this field right.
        detected_language: { $ref: '#/$defs/OptionalOutputLanguageCode' },
        assignment_generation: { $ref: '#/$defs/OptionalGeneration' },
        started_at_ms: { $ref: '#/$defs/EpochMillis' },
        ended_at_ms: { $ref: '#/$defs/EpochMillis' },
        text: { type: 'string', minLength: 1 },
        deepgram_segment_id: { $ref: '#/$defs/OpaqueId' },
        deepgram_confidence: { type: ['number', 'null'], minimum: 0, maximum: 1 },
        finality: { type: 'string', enum: [...FINALITIES] },
        created_at: { $ref: '#/$defs/Rfc3339Timestamp' },
      },
    },
    TranslationRevisionV1: {
      type: 'object',
      additionalProperties: false,
      required: [
        'contract_version',
        'translation_revision_id',
        'source_turn_id',
        'call_id',
        'target_language',
        'revision_index',
        'text',
        'delivery_state',
        'output_started_at_ms',
        'output_ended_at_ms',
        'output_artifact_ref',
        'translation_session_id',
        'translation_response_id',
        'model_version',
        'request_contract_version',
        'assignment_generation',
        'route_generation',
        'nuance_annotations',
        'created_at',
      ],
      properties: {
        contract_version: { const: TRANSLATION_REVISION_V1_CONTRACT_VERSION },
        translation_revision_id: { $ref: '#/$defs/UuidV5' },
        source_turn_id: { $ref: '#/$defs/UuidV5' },
        call_id: { $ref: '#/$defs/Uuid' },
        target_language: { $ref: '#/$defs/OutputLanguageCode' },
        revision_index: { type: 'integer', minimum: 0, maximum: GENERATION_MAX },
        text: { type: 'string', minLength: 1 },
        delivery_state: { type: 'string', enum: [...DELIVERY_STATES] },
        output_started_at_ms: { $ref: '#/$defs/OptionalEpochMillis' },
        output_ended_at_ms: { $ref: '#/$defs/OptionalEpochMillis' },
        output_artifact_ref: { type: ['string', 'null'], minLength: 1, maxLength: 512 },
        translation_session_id: { type: ['string', 'null'], minLength: 1, maxLength: 256 },
        translation_response_id: { type: ['string', 'null'], minLength: 1, maxLength: 256 },
        model_version: { type: ['string', 'null'], minLength: 1, maxLength: 256 },
        request_contract_version: { type: ['string', 'null'], minLength: 1, maxLength: 128 },
        assignment_generation: { $ref: '#/$defs/Generation' },
        route_generation: { $ref: '#/$defs/Generation' },
        nuance_annotations: {
          type: 'array',
          maxItems: 32,
          items: {
            type: 'object',
            additionalProperties: false,
            required: ['kind', 'note'],
            properties: {
              kind: { type: 'string', enum: [...NUANCE_KINDS] },
              note: { type: ['string', 'null'], minLength: 1, maxLength: 2000 },
            },
          },
        },
        created_at: { $ref: '#/$defs/Rfc3339Timestamp' },
      },
    },
  },
};

// ===========================================================================
// Validation engine
// ===========================================================================

export interface ContractValidationError {
  /** JSON Pointer to the failing instance location. `''` is the root. */
  readonly instancePath: string;
  /** The JSON Schema keyword that failed, e.g. `enum`, `type`, `required`, `minimum`. */
  readonly keyword: string;
  readonly message: string;
  readonly params: Readonly<Record<string, unknown>>;
}

export interface ContractValidationResult {
  readonly valid: boolean;
  readonly errors: readonly ContractValidationError[];
}

export class ContractViolationError extends Error {
  readonly contract: string;
  readonly errors: readonly ContractValidationError[];

  constructor(contract: string, errors: readonly ContractValidationError[]) {
    super(
      `${contract}: ${errors.length} contract violation(s): ` +
        errors.map((error) => `${error.instancePath || '<root>'} ${error.keyword}`).join(', '),
    );
    this.name = 'ContractViolationError';
    this.contract = contract;
    this.errors = errors;
  }
}

/**
 * Every keyword this interpreter implements, plus the annotations that carry no
 * assertion. A schema containing anything else is REFUSED at load time rather
 * than silently under-validated — a validator that ignores a keyword can never
 * go red for the constraint it dropped.
 */
const SUPPORTED_KEYWORDS = new Set([
  '$ref',
  '$defs',
  'type',
  'enum',
  'const',
  'required',
  'properties',
  'additionalProperties',
  'items',
  'minItems',
  'maxItems',
  'minLength',
  'maxLength',
  'pattern',
  'minimum',
  'maximum',
  'exclusiveMinimum',
  'exclusiveMaximum',
  'allOf',
  'anyOf',
  'oneOf',
  'not',
  'if',
  'then',
  'else',
  '$schema',
  '$id',
  '$comment',
  'title',
  'description',
]);

const SCHEMA_MAP_KEYWORDS = new Set(['properties', '$defs']);
const SCHEMA_LIST_KEYWORDS = new Set(['allOf', 'anyOf', 'oneOf']);
const SCHEMA_VALUED_KEYWORDS = new Set(['if', 'then', 'else', 'items', 'not', 'additionalProperties']);

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function assertSupportedSchemaKeywords(node: unknown, at = '#'): void {
  if (!isPlainObject(node)) {
    return;
  }
  for (const [keyword, value] of Object.entries(node)) {
    if (!SUPPORTED_KEYWORDS.has(keyword)) {
      throw new Error(
        `live-translation contract binding does not implement JSON Schema keyword "${keyword}" (at ${at}). ` +
          'Implement it in the validator before adding it to a schema — silently ignoring it under-validates.',
      );
    }
    if (SCHEMA_MAP_KEYWORDS.has(keyword) && isPlainObject(value)) {
      for (const [name, sub] of Object.entries(value)) {
        assertSupportedSchemaKeywords(sub, `${at}/${keyword}/${name}`);
      }
    } else if (SCHEMA_LIST_KEYWORDS.has(keyword) && Array.isArray(value)) {
      value.forEach((sub, index) => assertSupportedSchemaKeywords(sub, `${at}/${keyword}/${index}`));
    } else if (SCHEMA_VALUED_KEYWORDS.has(keyword) && isPlainObject(value)) {
      assertSupportedSchemaKeywords(value, `${at}/${keyword}`);
    }
  }
}

function jsonEquals(left: unknown, right: unknown): boolean {
  if (left === right) {
    return true;
  }
  if (Array.isArray(left) && Array.isArray(right)) {
    return left.length === right.length && left.every((item, index) => jsonEquals(item, right[index]));
  }
  if (isPlainObject(left) && isPlainObject(right)) {
    const leftKeys = Object.keys(left);
    const rightKeys = Object.keys(right);
    return (
      leftKeys.length === rightKeys.length &&
      leftKeys.every((key) => Object.prototype.hasOwnProperty.call(right, key) && jsonEquals(left[key], right[key]))
    );
  }
  return false;
}

function matchesType(type: string, data: unknown): boolean {
  switch (type) {
    case 'object':
      return isPlainObject(data);
    case 'array':
      return Array.isArray(data);
    case 'string':
      return typeof data === 'string';
    case 'integer':
      return typeof data === 'number' && Number.isInteger(data);
    case 'number':
      // Stricter than JSON allows on purpose: JSON has no NaN/Infinity, so no
      // contract vector can distinguish this from ajv's `typeof x === 'number'`.
      return typeof data === 'number' && Number.isFinite(data);
    case 'boolean':
      return typeof data === 'boolean';
    case 'null':
      return data === null;
    default:
      throw new Error(`live-translation contract binding does not implement JSON Schema type "${type}"`);
  }
}

const compiledPatterns = new Map<string, RegExp>();

function compilePattern(pattern: string): RegExp {
  const cached = compiledPatterns.get(pattern);
  if (cached) {
    return cached;
  }
  const compiled = new RegExp(pattern, 'u');
  compiledPatterns.set(pattern, compiled);
  return compiled;
}

function escapePointerToken(token: string): string {
  return token.replace(/~/g, '~0').replace(/\//g, '~1');
}

function resolvePointer(root: JsonSchema, pointer: string): JsonSchema {
  if (!pointer.startsWith('#')) {
    throw new Error(`live-translation contract binding supports only internal references, got "${pointer}"`);
  }
  let current: unknown = root;
  for (const rawSegment of pointer.slice(1).split('/')) {
    if (rawSegment === '') {
      continue;
    }
    const segment = rawSegment.replace(/~1/g, '/').replace(/~0/g, '~');
    if (!isPlainObject(current) || !Object.prototype.hasOwnProperty.call(current, segment)) {
      throw new Error(`live-translation contract pointer "${pointer}" does not resolve (stopped at "${segment}")`);
    }
    current = current[segment];
  }
  if (!isPlainObject(current)) {
    throw new Error(`live-translation contract pointer "${pointer}" does not name a schema object`);
  }
  return current;
}

/** ajv treats an explicitly-undefined property as absent; JSON cannot produce one, but a JS caller can. */
function hasValue(data: Record<string, unknown>, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(data, key) && data[key] !== undefined;
}

function makeError(
  instancePath: string,
  keyword: string,
  message: string,
  params: Record<string, unknown>,
): ContractValidationError {
  return { instancePath, keyword, message, params };
}

function validateNode(
  node: JsonSchema,
  data: unknown,
  path: string,
  root: JsonSchema,
  errors: ContractValidationError[],
): void {
  if (typeof node.$ref === 'string') {
    validateNode(resolvePointer(root, node.$ref), data, path, root, errors);
  }

  if (node.type !== undefined) {
    const declared = Array.isArray(node.type) ? node.type : [node.type];
    if (!declared.some((entry) => matchesType(String(entry), data))) {
      errors.push(makeError(path, 'type', `must be ${declared.join(' or ')}`, { type: node.type }));
    }
  }

  if (Object.prototype.hasOwnProperty.call(node, 'const') && !jsonEquals(data, node.const)) {
    errors.push(makeError(path, 'const', 'must be equal to constant', { allowedValue: node.const }));
  }

  if (Array.isArray(node.enum) && !node.enum.some((allowed) => jsonEquals(data, allowed))) {
    errors.push(makeError(path, 'enum', 'must be equal to one of the allowed values', { allowedValues: node.enum }));
  }

  if (typeof data === 'string') {
    if (typeof node.pattern === 'string' && !compilePattern(node.pattern).test(data)) {
      errors.push(makeError(path, 'pattern', `must match pattern "${node.pattern}"`, { pattern: node.pattern }));
    }
    // Only length assertions need a code-point count. Pattern/enum/type-only
    // nodes must not traverse or allocate a character array for caption text.
    if (typeof node.minLength === 'number' || typeof node.maxLength === 'number') {
      let codePoints: number;
      if (node.minLength === 1 && typeof node.maxLength !== 'number') {
        // Every nonempty UTF-16 string has at least one Unicode code point,
        // including an astral pair or a lone surrogate. No other threshold
        // uses code-unit length in place of code-point length.
        codePoints = data.length === 0 ? 0 : 1;
      } else {
        codePoints = 0;
        for (const _codePoint of data) codePoints += 1;
      }
      if (typeof node.minLength === 'number' && codePoints < node.minLength) {
        errors.push(
          makeError(path, 'minLength', `must NOT have fewer than ${node.minLength} characters`, {
            limit: node.minLength,
          }),
        );
      }
      if (typeof node.maxLength === 'number' && codePoints > node.maxLength) {
        errors.push(
          makeError(path, 'maxLength', `must NOT have more than ${node.maxLength} characters`, {
            limit: node.maxLength,
          }),
        );
      }
    }
  }

  if (typeof data === 'number' && Number.isFinite(data)) {
    if (typeof node.minimum === 'number' && data < node.minimum) {
      errors.push(makeError(path, 'minimum', `must be >= ${node.minimum}`, { comparison: '>=', limit: node.minimum }));
    }
    if (typeof node.maximum === 'number' && data > node.maximum) {
      errors.push(makeError(path, 'maximum', `must be <= ${node.maximum}`, { comparison: '<=', limit: node.maximum }));
    }
    if (typeof node.exclusiveMinimum === 'number' && data <= node.exclusiveMinimum) {
      errors.push(
        makeError(path, 'exclusiveMinimum', `must be > ${node.exclusiveMinimum}`, {
          comparison: '>',
          limit: node.exclusiveMinimum,
        }),
      );
    }
    if (typeof node.exclusiveMaximum === 'number' && data >= node.exclusiveMaximum) {
      errors.push(
        makeError(path, 'exclusiveMaximum', `must be < ${node.exclusiveMaximum}`, {
          comparison: '<',
          limit: node.exclusiveMaximum,
        }),
      );
    }
  }

  if (Array.isArray(data)) {
    if (typeof node.minItems === 'number' && data.length < node.minItems) {
      errors.push(
        makeError(path, 'minItems', `must NOT have fewer than ${node.minItems} items`, { limit: node.minItems }),
      );
    }
    if (typeof node.maxItems === 'number' && data.length > node.maxItems) {
      errors.push(
        makeError(path, 'maxItems', `must NOT have more than ${node.maxItems} items`, { limit: node.maxItems }),
      );
    }
    if (isPlainObject(node.items)) {
      const itemSchema = node.items as JsonSchema;
      data.forEach((item, index) => validateNode(itemSchema, item, `${path}/${index}`, root, errors));
    }
  }

  if (isPlainObject(data)) {
    if (Array.isArray(node.required)) {
      for (const key of node.required) {
        if (typeof key === 'string' && !hasValue(data, key)) {
          errors.push(makeError(path, 'required', `must have required property '${key}'`, { missingProperty: key }));
        }
      }
    }
    const properties = isPlainObject(node.properties) ? node.properties : undefined;
    if (properties) {
      for (const [name, sub] of Object.entries(properties)) {
        if (isPlainObject(sub) && hasValue(data, name)) {
          validateNode(sub as JsonSchema, data[name], `${path}/${escapePointerToken(name)}`, root, errors);
        }
      }
    }
    if (node.additionalProperties === false) {
      for (const name of Object.keys(data)) {
        if (!properties || !Object.prototype.hasOwnProperty.call(properties, name)) {
          errors.push(
            makeError(path, 'additionalProperties', 'must NOT have additional properties', {
              additionalProperty: name,
            }),
          );
        }
      }
    } else if (isPlainObject(node.additionalProperties)) {
      const extraSchema = node.additionalProperties as JsonSchema;
      for (const name of Object.keys(data)) {
        if (!properties || !Object.prototype.hasOwnProperty.call(properties, name)) {
          validateNode(extraSchema, data[name], `${path}/${escapePointerToken(name)}`, root, errors);
        }
      }
    }
  }

  if (Array.isArray(node.allOf)) {
    for (const sub of node.allOf) {
      if (isPlainObject(sub)) {
        validateNode(sub as JsonSchema, data, path, root, errors);
      }
    }
  }

  if (Array.isArray(node.anyOf)) {
    const anyPassed = node.anyOf.some(
      (sub) => isPlainObject(sub) && isValidAgainst(sub as JsonSchema, data, path, root),
    );
    if (!anyPassed) {
      errors.push(makeError(path, 'anyOf', 'must match a schema in anyOf', {}));
    }
  }

  if (Array.isArray(node.oneOf)) {
    let passing = 0;
    for (const sub of node.oneOf) {
      if (isPlainObject(sub) && isValidAgainst(sub as JsonSchema, data, path, root)) {
        passing += 1;
      }
    }
    if (passing !== 1) {
      // Only the `oneOf` error is reported, never the failing branches' errors:
      // branch errors would be noise that could accidentally satisfy a
      // "rejected for the right reason" check. Call the specific validator
      // (validateSourceTurnV1 / validateTranslationRevisionV1) for detail.
      errors.push(makeError(path, 'oneOf', 'must match exactly one schema in oneOf', { passingSchemas: passing }));
    }
  }

  if (isPlainObject(node.not) && isValidAgainst(node.not as JsonSchema, data, path, root)) {
    errors.push(makeError(path, 'not', 'must NOT be valid', {}));
  }

  if (isPlainObject(node.if)) {
    // Errors from evaluating `if` are discarded; only the taken branch reports.
    const conditionHolds = isValidAgainst(node.if as JsonSchema, data, path, root);
    const branch = conditionHolds ? node.then : node.else;
    if (isPlainObject(branch)) {
      validateNode(branch as JsonSchema, data, path, root, errors);
    }
  }
}

function isValidAgainst(node: JsonSchema, data: unknown, path: string, root: JsonSchema): boolean {
  const scratch: ContractValidationError[] = [];
  validateNode(node, data, path, root, scratch);
  return scratch.length === 0;
}

function deepFreeze<T>(value: T): T {
  if (value !== null && typeof value === 'object') {
    for (const child of Object.values(value as Record<string, unknown>)) {
      deepFreeze(child);
    }
    Object.freeze(value);
  }
  return value;
}

export type ContractSchemaName = 'route' | 'turn';

/** The frozen contracts, keyed exactly as `conformance-v1.json#/schemas` names them. */
export const CONTRACT_SCHEMAS: Record<ContractSchemaName, JsonSchema> = deepFreeze({
  route: ROUTE_V1_SCHEMA,
  turn: TURN_V1_SCHEMA,
});

assertSupportedSchemaKeywords(CONTRACT_SCHEMAS.route, 'route.v1');
assertSupportedSchemaKeywords(CONTRACT_SCHEMAS.turn, 'turn.v1');

/**
 * Validate against the subschema at a JSON Pointer, resolving internal `$ref`s
 * against the whole document — the `how_to_run` contract in
 * `conformance-v1.json`. Prefer the named validators below in product code.
 */
export function validateByContractRef(
  schema: ContractSchemaName,
  pointer: string,
  data: unknown,
): ContractValidationResult {
  const root = Object.prototype.hasOwnProperty.call(CONTRACT_SCHEMAS, schema) ? CONTRACT_SCHEMAS[schema] : undefined;
  if (!root) {
    throw new Error(`unknown live-translation contract schema "${schema}" (expected route or turn)`);
  }
  const target = pointer === '' || pointer === '#' ? root : resolvePointer(root, pointer);
  const errors: ContractValidationError[] = [];
  validateNode(target, data, '', root, errors);
  return { valid: errors.length === 0, errors };
}

export function validateRouteEnvelopeV1(data: unknown): ContractValidationResult {
  return validateByContractRef('route', '#', data);
}

export function validateSourceTurnV1(data: unknown): ContractValidationResult {
  return validateByContractRef('turn', '#/$defs/SourceTurnV1', data);
}

export function validateTranslationRevisionV1(data: unknown): ContractValidationResult {
  return validateByContractRef('turn', '#/$defs/TranslationRevisionV1', data);
}

export function validateTurnRecordV1(data: unknown): ContractValidationResult {
  return validateByContractRef('turn', '#', data);
}

/** A required, non-null translation output language. Deepgram `multi` is not a member. */
export function validateOutputLanguageCode(data: unknown): ContractValidationResult {
  return validateByContractRef('route', '#/$defs/OutputLanguageCode', data);
}

/** A translation output language or an explicit `null` meaning "not yet known". */
export function validateOptionalOutputLanguageCode(data: unknown): ContractValidationResult {
  return validateByContractRef('route', '#/$defs/OptionalOutputLanguageCode', data);
}

export function isRouteEnvelopeV1(data: unknown): data is RouteEnvelopeV1 {
  return validateRouteEnvelopeV1(data).valid;
}

export function isSourceTurnV1(data: unknown): data is SourceTurnV1 {
  return validateSourceTurnV1(data).valid;
}

export function isTranslationRevisionV1(data: unknown): data is TranslationRevisionV1 {
  return validateTranslationRevisionV1(data).valid;
}

export function isTurnRecordV1(data: unknown): data is TurnRecordV1 {
  return validateTurnRecordV1(data).valid;
}

export function assertRouteEnvelopeV1(data: unknown): RouteEnvelopeV1 {
  const result = validateRouteEnvelopeV1(data);
  if (!result.valid) {
    throw new ContractViolationError(ROUTE_ENVELOPE_V1_CONTRACT_VERSION, result.errors);
  }
  return data as RouteEnvelopeV1;
}

export function assertSourceTurnV1(data: unknown): SourceTurnV1 {
  const result = validateSourceTurnV1(data);
  if (!result.valid) {
    throw new ContractViolationError(SOURCE_TURN_V1_CONTRACT_VERSION, result.errors);
  }
  return data as SourceTurnV1;
}

export function assertTranslationRevisionV1(data: unknown): TranslationRevisionV1 {
  const result = validateTranslationRevisionV1(data);
  if (!result.valid) {
    throw new ContractViolationError(TRANSLATION_REVISION_V1_CONTRACT_VERSION, result.errors);
  }
  return data as TranslationRevisionV1;
}
